import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgUrl } from "@/lib/api";

/**
 * Image lightbox. Pass `src` for a single image, or `images` (array) + `index`
 * for a navigable gallery.
 */
export function Lightbox({ src, images, index = 0, alt = "", onClose }) {
  const raw = images && images.length ? images : src ? [src] : [];
  const list = raw.map((it) => (typeof it === "string" ? { url: it, label: "" } : it));
  const [i, setI] = useState(index);

  useEffect(() => setI(index), [index, src]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((x) => (x + 1) % list.length);
      if (e.key === "ArrowLeft") setI((x) => (x - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, list.length]);

  if (!list.length) return null;
  const multi = list.length > 1;
  const cur = list[i] || list[0];
  const labelText = cur.label === "pred" ? "PRED" : cur.label === "po" ? "PO" : "";

  return (
    <div onClick={onClose} data-testid="lightbox" className="fixed inset-0 z-[2000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in">
      <button onClick={onClose} data-testid="lightbox-close" className="absolute top-6 right-6 text-white/70 hover:text-gold transition-colors z-10">
        <X className="w-8 h-8" />
      </button>
      {multi && (
        <button onClick={(e) => { e.stopPropagation(); setI((x) => (x - 1 + list.length) % list.length); }} data-testid="lightbox-prev" className="absolute left-4 md:left-8 text-white/70 hover:text-gold transition-colors z-10">
          <ChevronLeft className="w-10 h-10" />
        </button>
      )}
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        {labelText && <span className="absolute top-3 left-3 bg-gold text-ink text-xs font-bold px-3 py-1 uppercase tracking-widest z-10">{labelText}</span>}
        <img src={imgUrl(cur.url)} alt={alt} className="max-h-[88vh] max-w-[92vw] object-contain border border-white/10 shadow-2xl" />
      </div>
      {multi && (
        <button onClick={(e) => { e.stopPropagation(); setI((x) => (x + 1) % list.length); }} data-testid="lightbox-next" className="absolute right-4 md:right-8 text-white/70 hover:text-gold transition-colors z-10">
          <ChevronRight className="w-10 h-10" />
        </button>
      )}
      {multi && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono2 text-white/60 text-sm tracking-widest">
          {i + 1} / {list.length}
        </div>
      )}
    </div>
  );
}

export default Lightbox;
